import React, { useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Environment, Grid, Box, Cylinder } from '@react-three/drei';
import { Save, Copy, Check } from 'lucide-react';

const BasicAGV = ({ config }) => {
  const {
    baseColor,
    cabinColor,
    mastColor,
    forkColor,
    wheelColor,
    chassisW,
    chassisL,
    chassisH,
    mastH,
    forkL,
    forkW,
    hasCabin,
    isReachTruck
  } = config;

  return (
    <group scale={0.02}>
      <group position={[0, chassisH / 2 + 200, 0]}> {/* +200 for wheel radius */}
        {/* Chassis */}
        <Box args={[chassisW, chassisH, chassisL]} castShadow receiveShadow>
          <meshStandardMaterial color={baseColor} />
        </Box>

        {/* Cabin */}
        {hasCabin && (
          <group position={[0, chassisH / 2, chassisL / 4]}>
            {/* Roof */}
            <Box args={[chassisW - 200, 100, chassisL / 2]} position={[0, 750, 0]} castShadow>
              <meshStandardMaterial color={cabinColor} />
            </Box>
            {/* Pillars */}
            <Box args={[100, 700, 100]} position={[-chassisW/2 + 150, 350, -chassisL/4 + 100]} castShadow><meshStandardMaterial color={cabinColor} /></Box>
            <Box args={[100, 700, 100]} position={[chassisW/2 - 150, 350, -chassisL/4 + 100]} castShadow><meshStandardMaterial color={cabinColor} /></Box>
            <Box args={[100, 700, 100]} position={[-chassisW/2 + 150, 350, chassisL/4 - 100]} castShadow><meshStandardMaterial color={cabinColor} /></Box>
            <Box args={[100, 700, 100]} position={[chassisW/2 - 150, 350, chassisL/4 - 100]} castShadow><meshStandardMaterial color={cabinColor} /></Box>
          </group>
        )}

        {/* Mast */}
        <Box 
          args={[chassisW - 200, mastH, 200]} 
          position={[0, mastH / 2 - chassisH / 2, isReachTruck ? -chassisL / 2 - 100 : -chassisL / 2 + 100]} 
          castShadow 
          receiveShadow
        >
          <meshStandardMaterial color={mastColor} />
        </Box>

        {/* Forks */}
        <group position={[0, -chassisH / 2 + 100, isReachTruck ? -chassisL / 2 - 200 : -chassisL / 2]}>
          <Box args={[forkW, 100, forkL]} position={[-chassisW/4, 0, -forkL/2]} castShadow receiveShadow>
            <meshStandardMaterial color={forkColor} />
          </Box>
          <Box args={[forkW, 100, forkL]} position={[chassisW/4, 0, -forkL/2]} castShadow receiveShadow>
            <meshStandardMaterial color={forkColor} />
          </Box>
        </group>

        {/* Wheels */}
        <Cylinder args={[200, 200, 200, 32]} rotation={[0, 0, Math.PI / 2]} position={[-chassisW/2 - 50, -chassisH/2, -chassisL/3]} castShadow><meshStandardMaterial color={wheelColor} /></Cylinder>
        <Cylinder args={[200, 200, 200, 32]} rotation={[0, 0, Math.PI / 2]} position={[chassisW/2 + 50, -chassisH/2, -chassisL/3]} castShadow><meshStandardMaterial color={wheelColor} /></Cylinder>
        {config.wheelCount === 4 ? (
          <>
            <Cylinder args={[200, 200, 200, 32]} rotation={[0, 0, Math.PI / 2]} position={[-chassisW/2 - 50, -chassisH/2, chassisL/3]} castShadow><meshStandardMaterial color={wheelColor} /></Cylinder>
            <Cylinder args={[200, 200, 200, 32]} rotation={[0, 0, Math.PI / 2]} position={[chassisW/2 + 50, -chassisH/2, chassisL/3]} castShadow><meshStandardMaterial color={wheelColor} /></Cylinder>
          </>
        ) : (
          <Cylinder args={[200, 200, 200, 32]} rotation={[0, 0, Math.PI / 2]} position={[0, -chassisH/2, chassisL/3]} castShadow><meshStandardMaterial color={wheelColor} /></Cylinder>
        )}
      </group>
    </group>
  );
};

const ReflexAGV = ({ config }) => {
  const {
    baseColor,
    cabinColor,
    mastColor,
    forkColor,
    wheelColor,
    chassisW,
    chassisL,
    chassisH,
    mastH,
    forkL,
    forkW,
    wheelCount,
    roofH = 2200,
    mastW = 150,
    pillarW = 100,
    legL = 1200,
    legY = 100
  } = config;

  // Reflex specific adjustments
  // Base height matches leg height to form a single solid chassis
  const bumperH = Math.max(100, legY * 2);
  const bodyH = Math.max(100, chassisH - bumperH);
  const legW = Math.max(100, chassisW * 0.15);

  return (
    <group scale={0.02}>
      <group position={[0, 150, 0]}> {/* lift up by drive wheel radius */}
        
        {/* Outriggers (Legs) extending forward */}
        <Box args={[legW, bumperH, legL]} position={[-chassisW/2 + legW/2, bumperH/2, chassisL*0.2 - legL/2]} castShadow receiveShadow>
          <meshStandardMaterial color={cabinColor} />
        </Box>
        <Box args={[legW, bumperH, legL]} position={[chassisW/2 - legW/2, bumperH/2, chassisL*0.2 - legL/2]} castShadow receiveShadow>
          <meshStandardMaterial color={cabinColor} />
        </Box>

        {/* Lower Bumper Base */}
        <Box args={[chassisW, bumperH, chassisL * 0.6]} position={[0, bumperH/2, chassisL * 0.2]} castShadow receiveShadow>
          <meshStandardMaterial color={cabinColor} />
        </Box>

        {/* Upper Main Body (Orange Shell) */}
        {/* Left Wall (Closed side) */}
        <Box args={[150, bodyH, chassisL * 0.7 - 100]} position={[-chassisW/2 + 75, bumperH + bodyH/2, chassisL * 0.15]} castShadow receiveShadow>
          <meshStandardMaterial color={baseColor} />
        </Box>
        {/* Right Wall (Seat side) */}
        <Box args={[150, bodyH, chassisL * 0.7 - 100]} position={[chassisW/2 - 75, bumperH + bodyH/2, chassisL * 0.15]} castShadow receiveShadow>
          <meshStandardMaterial color={baseColor} />
        </Box>
        {/* Front Lower Wall (behind mast) */}
        <Box args={[chassisW, bodyH - 100, 150]} position={[0, bumperH + (bodyH-100)/2, -chassisL * 0.2]} castShadow receiveShadow>
          <meshStandardMaterial color={baseColor} />
        </Box>
        
        {/* Rear Entry Step (Dark Grey) */}
        <Box args={[chassisW - 300, 100, 200]} position={[0, bumperH + 50, chassisL/2 - 100]} castShadow receiveShadow>
          <meshStandardMaterial color={cabinColor} />
        </Box>

        {/* Driver Cabin (Interior) */}
        {/* Console / Dashboard */}
        <Box args={[chassisW - 300, 400, 250]} position={[0, bumperH + 200, -chassisL*0.05]} castShadow>
          <meshStandardMaterial color={cabinColor} />
        </Box>
        {/* Steering / Small Screen */}
        <Box args={[150, 50, 150]} position={[-chassisW/4 + 100, bumperH + 450, -chassisL*0.1]} rotation={[0.2, 0, 0]} castShadow>
          <meshStandardMaterial color="#111827" />
        </Box>

        {/* Seat */}
        <Box args={[350, 150, 350]} position={[chassisW/2 - 250, bumperH + 150, chassisL*0.2]} castShadow>
          <meshStandardMaterial color="#374151" />
        </Box>
        {/* Seat Backrest */}
        <Box args={[100, 350, 350]} position={[chassisW/2 - 125, bumperH + 400, chassisL*0.2]} castShadow>
          <meshStandardMaterial color="#374151" />
        </Box>

        {/* Overhead Guard (Roof) */}
        {/* Rear Right Pillar */}
        <Box args={[pillarW, roofH - chassisH, pillarW * 1.5]} position={[chassisW/2 - pillarW/2, chassisH + (roofH - chassisH)/2, chassisL/2 - pillarW]} castShadow>
          <meshStandardMaterial color={cabinColor} />
        </Box>
        {/* Rear Left Pillar */}
        <Box args={[pillarW, roofH - chassisH, pillarW * 1.5]} position={[-chassisW/2 + pillarW/2, chassisH + (roofH - chassisH)/2, chassisL/2 - pillarW]} castShadow>
          <meshStandardMaterial color={cabinColor} />
        </Box>
        {/* Front Right Pillar */}
        <Cylinder args={[pillarW/2.5, pillarW/2.5, roofH - chassisH, 16]} position={[chassisW/2 - pillarW/2, chassisH + (roofH - chassisH)/2, -chassisL*0.1]} rotation={[0.1, 0, 0]} castShadow>
          <meshStandardMaterial color={cabinColor} />
        </Cylinder>
        {/* Front Left Pillar */}
        <Cylinder args={[pillarW/2.5, pillarW/2.5, roofH - chassisH, 16]} position={[-chassisW/2 + pillarW/2, chassisH + (roofH - chassisH)/2, -chassisL*0.1]} rotation={[0.1, 0, 0]} castShadow>
          <meshStandardMaterial color={cabinColor} />
        </Cylinder>
        {/* Roof Frame */}
        <Box args={[chassisW, 50, chassisL * 0.7]} position={[0, roofH, chassisL * 0.15 - 50]} rotation={[-0.05, 0, 0]} castShadow>
          <meshStandardMaterial color={cabinColor} />
        </Box>
        {/* Roof Glass (Clear Cutout) */}
        <Box args={[chassisW - 200, 60, chassisL * 0.4]} position={[0, roofH, chassisL * 0.1]} rotation={[-0.05, 0, 0]} castShadow>
          <meshStandardMaterial color="#94a3b8" transparent opacity={0.3} />
        </Box>

        {/* Multi-stage Mast */}
        <group position={[0, 0, -chassisL * 0.25]}>
          {/* Outer Mast */}
          <Box args={[chassisW - legW*2 - 50, mastH, mastW]} position={[0, mastH/2, 0]} castShadow receiveShadow>
            <meshStandardMaterial color={mastColor} />
          </Box>
          {/* Inner Mast */}
          <Box args={[chassisW - legW*2 - 150, mastH * 0.9, mastW * 0.7]} position={[0, mastH * 0.45 + 300, -mastW*0.3]} castShadow receiveShadow>
            <meshStandardMaterial color={mastColor} />
          </Box>
          
          {/* Carriage & Forks */}
          <group position={[0, 300, -100]}>
            <Box args={[chassisW - legW*2 - 50, 200, 100]} position={[0, 0, 0]} castShadow receiveShadow>
              <meshStandardMaterial color={mastColor} />
            </Box>
            <Box args={[forkW, 100, forkL]} position={[-chassisW/4, -50, -forkL/2]} castShadow receiveShadow>
              <meshStandardMaterial color={forkColor} />
            </Box>
            <Box args={[forkW, 100, forkL]} position={[chassisW/4, -50, -forkL/2]} castShadow receiveShadow>
              <meshStandardMaterial color={forkColor} />
            </Box>
          </group>
        </group>

        {/* Wheels */}
        {/* Load Wheels (at the tip of outriggers) */}
        <Cylinder args={[100, 100, legW + 20, 32]} rotation={[0, 0, Math.PI / 2]} position={[-chassisW/2 + legW/2, -50, chassisL*0.2 - legL + 100]} castShadow>
          <meshStandardMaterial color={wheelColor} />
        </Cylinder>
        <Cylinder args={[100, 100, legW + 20, 32]} rotation={[0, 0, Math.PI / 2]} position={[chassisW/2 - legW/2, -50, chassisL*0.2 - legL + 100]} castShadow>
          <meshStandardMaterial color={wheelColor} />
        </Cylinder>
        
        {/* Drive/Rear Wheel(s) */}
        {wheelCount === 4 ? (
          <>
            <Cylinder args={[150, 150, 200, 32]} rotation={[0, 0, Math.PI / 2]} position={[-chassisW/2 + 150, 0, chassisL/2 - 100]} castShadow>
              <meshStandardMaterial color={wheelColor} />
            </Cylinder>
            <Cylinder args={[150, 150, 200, 32]} rotation={[0, 0, Math.PI / 2]} position={[chassisW/2 - 150, 0, chassisL/2 - 100]} castShadow>
              <meshStandardMaterial color={wheelColor} />
            </Cylinder>
          </>
        ) : (
          <Cylinder args={[150, 150, 250, 32]} rotation={[0, 0, Math.PI / 2]} position={[0, 0, chassisL/2 - 150]} castShadow>
            <meshStandardMaterial color={wheelColor} />
          </Cylinder>
        )}
      </group>
    </group>
  );
};

const AGVPreview = ({ config }) => {
  return config.style === 'reflex' ? <ReflexAGV config={config} /> : <BasicAGV config={config} />;
};

export default function AGVDesigner() {
  const [copied, setCopied] = useState(false);
  const [config, setConfig] = useState({
    name: 'Reflex Reach Truck',
    style: 'reflex',
    baseColor: '#ea580c', // Orange
    cabinColor: '#1f2937', // Dark Grey
    mastColor: '#111827', // Black
    forkColor: '#94a3b8',
    wheelColor: '#c2410c', // Polyurethane orange
    chassisW: 1100,
    chassisL: 1700,
    chassisH: 600,
    roofH: 2200,
    pillarW: 100,
    mastH: 2200,
    mastW: 150,
    legL: 1200,
    legY: 100,
    forkL: 1200,
    forkW: 200,
    hasCabin: true,
    isReachTruck: true,
    wheelCount: 3
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let parsedValue = value;
    if (type === 'checkbox') parsedValue = checked;
    else if (type === 'number' || type === 'range' || type === 'radio') {
       if (!isNaN(value) && name !== 'style') parsedValue = Number(value);
    }
    setConfig(prev => ({
      ...prev,
      [name]: parsedValue
    }));
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(config, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const ConfigGroup = ({ title, children }) => (
    <div className="mb-6 bg-gray-50 p-4 rounded-xl border border-gray-100 shadow-sm">
      <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">{title}</h3>
      <div className="space-y-4">
        {children}
      </div>
    </div>
  );

  const SliderInput = ({ label, name, min, max }) => (
    <div>
      <div className="flex justify-between mb-1">
        <label className="text-xs font-medium text-gray-600">{label}</label>
        <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded">{config[name]} mm</span>
      </div>
      <input type="range" name={name} min={min} max={max} step="50" value={config[name]} onChange={handleChange} className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600" />
    </div>
  );

  const ColorInput = ({ label, name }) => (
    <div className="flex items-center justify-between">
      <label className="text-xs font-medium text-gray-600">{label}</label>
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-400 font-mono uppercase">{config[name]}</span>
        <input type="color" name={name} value={config[name]} onChange={handleChange} className="w-8 h-8 rounded cursor-pointer border-0 p-0" />
      </div>
    </div>
  );

  return (
    <div className="flex h-[calc(100vh-64px)] bg-white overflow-hidden" style={{ height: 'calc(100vh - 64px)' }}>
      {/* Control Panel (Left) */}
      <div className="w-96 border-r flex flex-col bg-white shadow-lg z-10 h-full">
        <div className="p-5 border-b bg-gradient-to-r from-blue-600 to-blue-800 text-white flex-shrink-0">
          <h2 className="text-xl font-bold mb-1">AGV Designer</h2>
          <p className="text-xs text-blue-100 opacity-80">Procedural 3D Model Builder</p>
        </div>
        
        <div className="flex-1 overflow-y-auto p-5 scrollbar-thin">
          <div className="mb-6">
            <label className="block text-xs font-bold text-gray-700 uppercase mb-2">Model Name</label>
            <input type="text" name="name" value={config.name} onChange={handleChange} className="w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:border-blue-500 focus:ring-blue-500 text-sm" />
          </div>

          <ConfigGroup title="Model Style">
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="style" value="reflex" checked={config.style === 'reflex'} onChange={handleChange} className="text-blue-600 focus:ring-blue-500" />
                <span className="text-sm font-medium text-gray-700">Reflex (Detailed)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="style" value="basic" checked={config.style === 'basic'} onChange={handleChange} className="text-blue-600 focus:ring-blue-500" />
                <span className="text-sm font-medium text-gray-700">Basic (Boxy)</span>
              </label>
            </div>
          </ConfigGroup>

          <ConfigGroup title="Dimensions (mm)">
            <SliderInput label="Chassis Width" name="chassisW" min="500" max="2000" />
            <SliderInput label="Chassis Length" name="chassisL" min="1000" max="3000" />
            <SliderInput label="Chassis Height" name="chassisH" min="200" max="1500" />
            <SliderInput label="Roof Height" name="roofH" min="1500" max="3500" />
            <SliderInput label="Roof Pillar Thickness" name="pillarW" min="40" max="200" />
            <SliderInput label="Mast Height" name="mastH" min="1000" max="4000" />
            <SliderInput label="Mast Thickness" name="mastW" min="100" max="400" />
            <SliderInput label="Outrigger Length" name="legL" min="500" max="3000" />
            <SliderInput label="Outrigger Height" name="legY" min="0" max="500" />
            <SliderInput label="Fork Length" name="forkL" min="500" max="2500" />
            <SliderInput label="Fork Width" name="forkW" min="100" max="500" />
          </ConfigGroup>

          <ConfigGroup title="Appearance (Colors)">
            <ColorInput label="Base (Orange)" name="baseColor" />
            <ColorInput label="Cabin/Bumper" name="cabinColor" />
            <ColorInput label="Mast" name="mastColor" />
            <ColorInput label="Forks" name="forkColor" />
            <ColorInput label="Wheels" name="wheelColor" />
          </ConfigGroup>

          <ConfigGroup title="Features">
            <div className="flex flex-col gap-2 mb-4">
              <label className="text-xs font-bold text-gray-700 uppercase">Wheel Configuration</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="wheelCount" value="4" checked={config.wheelCount === 4} onChange={handleChange} className="text-blue-600 focus:ring-blue-500" />
                  <span className="text-sm font-medium text-gray-700">4 Wheels</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="wheelCount" value="3" checked={config.wheelCount === 3} onChange={handleChange} className="text-blue-600 focus:ring-blue-500" />
                  <span className="text-sm font-medium text-gray-700">3 Wheels</span>
                </label>
              </div>
            </div>
            {config.style === 'basic' && (
              <>
                <label className="flex items-center gap-3 cursor-pointer p-2 hover:bg-white rounded-lg border border-transparent hover:border-gray-200 transition-colors">
                  <input type="checkbox" name="hasCabin" checked={config.hasCabin} onChange={handleChange} className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" />
                  <span className="text-sm font-medium text-gray-700">Driver Cabin / Roof</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer p-2 hover:bg-white rounded-lg border border-transparent hover:border-gray-200 transition-colors">
                  <input type="checkbox" name="isReachTruck" checked={config.isReachTruck} onChange={handleChange} className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" />
                  <span className="text-sm font-medium text-gray-700">Reach Truck (Extending Mast)</span>
                </label>
              </>
            )}
          </ConfigGroup>
        </div>

        {/* JSON Export Footer */}
        <div className="p-4 border-t bg-gray-50 flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-gray-500 uppercase">JSON Profile</span>
            <button onClick={handleCopy} className="flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-md transition-colors">
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Copied!' : 'Copy JSON'}
            </button>
          </div>
          <pre className="bg-gray-800 text-green-400 p-3 rounded-lg text-[10px] h-32 overflow-y-auto font-mono shadow-inner border border-gray-700">
            {JSON.stringify(config, null, 2)}
          </pre>
        </div>
      </div>

      {/* 3D Preview (Right) */}
      <div className="flex-1 bg-gradient-to-br from-gray-100 to-gray-300 relative h-full">
        {/* Toolbar overlay */}
        <div className="absolute top-4 right-4 z-10 flex gap-2">
          <div className="bg-white/80 backdrop-blur-sm px-4 py-2 rounded-lg shadow-sm border border-white/40 text-sm font-medium text-gray-600 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
            Real-time Preview
          </div>
        </div>

        <Canvas shadows camera={{ position: [50, 40, 50], fov: 45 }}>
          <ambientLight intensity={0.5} />
          <directionalLight 
            position={[50, 100, 50]} 
            intensity={1.5} 
            castShadow 
            shadow-mapSize={[2048, 2048]}
            shadow-camera-near={0.5}
            shadow-camera-far={500}
            shadow-camera-left={-100}
            shadow-camera-right={100}
            shadow-camera-top={100}
            shadow-camera-bottom={-100}
          />
          <Environment preset="city" />
          
          <AGVPreview config={config} />
          
          <Grid 
            args={[100, 100]} 
            position={[0, 0, 0]} 
            cellColor="#cbd5e1" 
            sectionColor="#94a3b8"
            fadeDistance={100}
            fadeStrength={1}
            infiniteGrid
          />
          <OrbitControls makeDefault minPolarAngle={0} maxPolarAngle={Math.PI / 2 - 0.05} />
        </Canvas>
      </div>
    </div>
  );
}
